/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getLocalStorageItem } from '../../shared-functions/LocalStorage';

interface ProductState {
  activeProduct: string | null;
  allowedFeatures: string[];
  currentLimits: any;
  availableProducts: any[];
}

const initialState: ProductState = {
  activeProduct: getLocalStorageItem('activeProduct') || null,
  allowedFeatures: getLocalStorageItem('allowedFeatures') || [],
  currentLimits: getLocalStorageItem('currentLimits') || {},
  availableProducts: getLocalStorageItem('availableProducts') || [],
};

/**
 * Product Slice for managing product context, features, and limits.
 */
const ProductSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    /**
     * Sets the currently active product (e.g., CPO or eMSP).
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setActiveProduct: (state, action: PayloadAction<string>) => {
      state.activeProduct = action.payload;
    },
    /**
     * Sets the allowed features for the active product context.
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setAllowedFeatures: (state, action: PayloadAction<string[]>) => {
      state.allowedFeatures = action.payload;
    },
    /**
     * Sets the usage limits for the active product.
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setCurrentLimits: (state, action: PayloadAction<any>) => {
      state.currentLimits = action.payload;
    },
    /**
     * Sets the list of all products the user has access to.
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setAvailableProducts: (state, action: PayloadAction<any[]>) => {
      state.availableProducts = action.payload;
    },
    /**
     * Resets the product state to initial values.
     * @param {any} state - The state
     */
    resetProduct: (state) => {
      state.activeProduct = null;
      state.allowedFeatures = [];
      state.currentLimits = {};
      state.availableProducts = [];
    },
  },
});

export const {
  setActiveProduct,
  setAllowedFeatures,
  setCurrentLimits,
  setAvailableProducts,
  resetProduct,
} = ProductSlice.actions;

export default ProductSlice.reducer;
